if (!customElements.get('astra-quick-choice')) {
  customElements.define('astra-quick-choice', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.product = document.getElementById(`MainProduct-${this.dataset.section}`);
      this.trigger = this.product?.querySelector('[data-astra-quick-open]');
      this.dialog = this.querySelector('dialog');
      if (!this.trigger || !this.dialog?.showModal) return;
      this.options = this.querySelector('[data-quick-options]');
      this.add = this.querySelector('[data-quick-add]');
      this.checkout = this.querySelector('[data-quick-checkout]');
      this.status = this.querySelector('[data-quick-status]');
      this.onTrigger = (event) => {
        if (!this.nativeButton) return;
        event.preventDefault();
        this.activate(this.trigger);
      };
      this.trigger.addEventListener('click', this.onTrigger);
      this.trigger.setAttribute('aria-haspopup', 'dialog');
      this.trigger.setAttribute('aria-controls', `QuickChoice-${this.dataset.section}`);
      this.dialog.id = `QuickChoice-${this.dataset.section}`;
      this.querySelector('[data-quick-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', (event) => {
        const box = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('ub-quick-choice-open');
        if (!document.querySelector('cart-drawer.active')) this.opener?.focus({ preventScroll: true });
      });
      this.options.addEventListener('change', (event) => this.select(event));
      this.add.addEventListener('click', () => this.submit());
      this.checkout.addEventListener('click', () => this.submit(true));
      this.onNativeChange = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (!this.product.pendingRequestUrl) this.pending = false;
          if (this.adding && this.sawLoading && !this.nativeButton?.classList.contains('loading')) this.fail(this.dataset.errorLabel);
          if (this.dialog.open) this.sync();
        });
      };
      this.observer = new MutationObserver(this.onNativeChange);
      const info = this.product.querySelector('.product__info-container');
      if (info) this.observer.observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'class', 'value', 'checked'] });
      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source, productVariantId }) => {
          if (!this.adding || source !== 'product-form' || String(productVariantId) !== this.submittedVariant) return;
          this.finish();
          if (this.dialog.open) this.dialog.close();
          const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
          cart?.setActiveElement?.(this.opener || this.trigger);
        }));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.cartError, ({ source, productVariantId, message, errors }) => {
          if (!this.adding || source !== 'product-form' || String(productVariantId) !== this.submittedVariant) return;
          this.fail(typeof errors === 'string' ? errors : message || this.dataset.errorLabel);
        }));
      }
    }

    get nativeButton() {
      return this.product?.querySelector('product-form form button[name="add"]');
    }

    get nativeForm() {
      return this.nativeButton?.form;
    }

    activate(opener) {
      if (!this.nativeButton || !this.dialog?.showModal || this.adding) return;
      this.opener = opener || this.trigger;
      this.open();
    }

    open() {
      this.sync();
      if (!this.dialog.open) {
        document.body.classList.add('ub-quick-choice-open');
        this.dialog.showModal();
        this.querySelector('[data-quick-close]').focus({ preventScroll: true });
      }
    }

    sync() {
      const title = this.product.querySelector('.astra-purchase-title') || this.product.querySelector('h1');
      if (title) this.setText(this.querySelector('h2'), title.textContent.trim());
      const price = this.product.querySelector(`[id="price-${this.dataset.section}"]`);
      const priceTarget = this.querySelector('[data-quick-price]');
      if (price) {
        const markup = price.innerHTML;
        if (this.priceMarkup !== markup) {
          this.priceMarkup = markup;
          priceTarget.innerHTML = markup;
        }
        priceTarget.hidden = price.classList.contains('hidden');
      }
      const dimensions = this.product.querySelector('.astra-purchase-specs');
      const dimensionsTarget = this.querySelector('[data-quick-dimensions]');
      const dimensionName = dimensions?.querySelector('dt')?.textContent.trim() || '';
      const dimensionLines = Array.from(dimensions?.querySelectorAll('.ub-dims__line') || [],
        line => Array.from(line.children, child => child.textContent.trim()).join(' : '));
      const dimensionKey = JSON.stringify([Boolean(dimensions), dimensionName, dimensionLines]);
      if (this.dimensionKey !== dimensionKey) {
        this.dimensionKey = dimensionKey;
        const fragment = document.createDocumentFragment();
        if (dimensions) {
          const heading = document.createElement('strong');
          heading.textContent = dimensionName;
          fragment.append(heading);
          dimensionLines.forEach(text => {
            const row = document.createElement('p');
            row.textContent = text;
            fragment.append(row);
          });
        }
        dimensionsTarget.replaceChildren(fragment);
      }
      if (!this.pending) this.syncOptions();
      this.syncImage();
      this.syncState();
    }

    setText(element, text) {
      if (element && element.textContent !== text) element.textContent = text;
    }

    setBusy(element, busy) {
      const value = String(Boolean(busy));
      if (element.getAttribute('aria-busy') !== value) element.setAttribute('aria-busy', value);
    }

    syncState() {
      const button = this.nativeButton;
      this.add.disabled = !button || button.disabled || this.pending || this.adding || button.getAttribute('aria-disabled') === 'true';
      this.setText(this.add, this.adding ? this.dataset.addingLabel : this.pending ? this.dataset.loadingLabel : button?.querySelector('span')?.textContent.trim() || this.dataset.addLabel);
      this.setBusy(this.add, this.adding || this.pending);
      this.checkout.disabled = this.add.disabled || !this.nativeForm?.querySelector('[data-astra-buy-now]');
      this.setText(this.checkout, this.adding && this.checkingOut ? this.dataset.checkoutLoadingLabel : this.dataset.checkoutLabel);
      this.setBusy(this.checkout, this.adding || this.pending);
      if (!this.error) {
        this.setText(this.status, this.pending ? this.dataset.loadingLabel : button?.disabled ? this.add.textContent : '');
        this.status.removeAttribute('data-error');
      }
    }

    syncOptions() {
      const picker = this.product.querySelector('variant-selects');
      const fingerprint = picker?.innerHTML || '';
      if (this.fingerprint === fingerprint) return;
      this.fingerprint = fingerprint;
      const active = this.options.contains(document.activeElement) ? document.activeElement.dataset.nativeId : this.selectionFocus;
      const fragment = document.createDocumentFragment();
      picker?.querySelectorAll('.product-form__input').forEach((group, groupIndex) => {
        const select = group.querySelector('select');
        const values = Array.from(select ? select.options : group.querySelectorAll('input[type="radio"]'));
        if (!values.length) return;
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('legend');
        const selected = values.find((value) => select ? value.selected : value.checked);
        const name = select ? select.name.trim().replace(/^options\[(.*)\]$/, '$1') : values[0].name.trim().replace(/-\d+$/, '');
        legend.textContent = `${name}${selected ? ` : ${selected.value}` : ''}`;
        fieldset.append(legend);
        const container = document.createElement('div');
        container.className = 'ub-quick-choice__values';
        values.forEach((value, index) => {
          const wrapper = document.createElement('div');
          wrapper.className = 'ub-quick-choice__value';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `quick-${this.dataset.section}-${groupIndex}`;
          input.id = `quick-${this.dataset.section}-${groupIndex}-${index}`;
          input.value = value.value;
          input.dataset.nativeId = value.dataset.optionValueId || value.id;
          input.checked = select ? value.selected : value.checked;
          input.disabled = value.disabled;
          const label = document.createElement('label');
          label.htmlFor = input.id;
          const nativeLabel = value.labels?.[0];
          const photo = nativeLabel?.querySelector('img') || this.querySelector('[data-quick-photos]')?.content.querySelector(`[data-quick-option-id="${CSS.escape(value.dataset.optionValueId || '')}"]`);
          if (photo) {
            const image = photo.cloneNode();
            image.removeAttribute('id');
            image.removeAttribute('class');
            image.alt = '';
            image.sizes = '86px';
            image.loading = 'eager';
            label.append(image);
            wrapper.classList.add('ub-quick-choice__value--photo');
          }
          const text = document.createElement('span');
          text.textContent = value.value;
          label.append(text);
          const unavailable = value.disabled || value.classList.contains('disabled') || value.classList.contains('visually-disabled');
          if (unavailable) {
            wrapper.setAttribute('data-unavailable', '');
            const badge = document.createElement('small');
            badge.textContent = this.dataset.unavailableLabel;
            label.append(badge);
          }
          wrapper.append(input, label);
          container.append(wrapper);
        });
        fieldset.append(container);
        fragment.append(fieldset);
      });
      this.options.replaceChildren(fragment);
      if (active && this.dialog.open) this.options.querySelector(`[data-native-id="${CSS.escape(active)}"]`)?.focus({ preventScroll: true });
      this.selectionFocus = null;
    }

    syncImage() {
      const image = this.querySelector('[data-quick-image]');
      const checkedPhoto = this.options.querySelector('input:checked + label img');
      const fallback = this.product.querySelector('[data-astra-variant-preview]');
      const source = checkedPhoto || fallback;
      if (!source) { image.hidden = true; return; }
      const src = source.currentSrc || source.src;
      if (image.getAttribute('src') !== src) image.src = src;
      image.hidden = false;
    }

    select(event) {
      const input = event.target.closest('input[data-native-id]');
      if (!input || this.pending || this.adding) return;
      const picker = this.product.querySelector('variant-selects');
      const key = CSS.escape(input.dataset.nativeId);
      const native = picker?.querySelector(`[data-option-value-id="${key}"], [id="${key}"]`);
      if (!native) { this.fail(this.dataset.errorLabel); return; }
      this.error = false;
      this.pending = true;
      this.selectionFocus = input.dataset.nativeId;
      this.options.querySelectorAll('input').forEach((control) => { control.disabled = true; });
      if (native.tagName === 'OPTION') {
        native.parentElement.value = native.value;
        native.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        native.checked = true;
        native.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.syncState();
      clearTimeout(this.optionTimer);
      this.optionTimer = setTimeout(() => {
        if (!this.pending) return;
        this.pending = false;
        this.fingerprint = null;
        this.sync();
        this.status.textContent = this.dataset.errorLabel;
        this.status.setAttribute('data-error', '');
      }, 15000);
    }

    submit(checkout = false) {
      const button = this.nativeButton;
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || this.pending || this.adding) return;
      const target = checkout ? this.nativeForm?.querySelector('[data-astra-buy-now]') : button;
      if (!target || target.disabled) return;
      const form = target.form;
      if (!form || !form.reportValidity()) return;
      this.error = false;
      this.checkingOut = checkout;
      this.adding = true;
      this.submittedVariant = String(this.nativeForm.querySelector('[name="id"]').value);
      this.trigger.setAttribute('aria-busy', 'true');
      this.opener?.setAttribute('aria-busy', 'true');
      this.syncState();
      form.requestSubmit(target);
      this.sawLoading = button.classList.contains('loading');
    }

    finish() {
      this.adding = false;
      this.sawLoading = false;
      this.trigger.removeAttribute('aria-busy');
      this.opener?.removeAttribute('aria-busy');
    }

    fail(message) {
      this.finish();
      this.error = true;
      this.open();
      this.status.textContent = message;
      this.status.setAttribute('data-error', '');
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.trigger?.removeEventListener('click', this.onTrigger);
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
      clearTimeout(this.optionTimer);
      cancelAnimationFrame(this.frame);
      if (this.dialog?.open) this.dialog.close();
    }
  });
}


